import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Cloud, Loader2, Shield, Lock, Eye, EyeOff } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [isSetup, setIsSetup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  useEffect(() => {
    // Check if already logged in
    const adminToken = localStorage.getItem("admin_token");
    if (adminToken) {
      navigate("/admin");
      return;
    }

    // Check if admin setup is complete
    checkAdminSetup();
  }, [navigate]);

  const checkAdminSetup = async () => {
    try {
      const response = await axios.get(`${API}/admin/auth/check-setup`);
      setIsSetup(response.data.setup_complete);
    } catch (error) {
      console.error("Failed to check admin setup:", error);
    } finally {
      setCheckingSetup(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const endpoint = isSetup ? "/admin/auth/login" : "/admin/auth/setup";
      const response = await axios.post(`${API}${endpoint}`, {
        email: formData.email,
        password: formData.password
      });

      localStorage.setItem("admin_token", response.data.token);
      localStorage.setItem("admin", JSON.stringify(response.data.admin));

      toast.success(isSetup ? "Login successful!" : "Admin account created!");
      navigate("/admin");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  if (checkingSetup) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-bronze" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-bronze/20 mb-4">
            <Shield className="w-8 h-8 text-bronze" />
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src="/logo.png" alt="CloudSync Pro" className="h-16 object-contain" />
          </div>
          <p className="text-slate-400">Admin Dashboard</p>
        </div>

        <Card className="bg-slate-800 border-slate-700 shadow-2xl">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-xl text-white">
              {isSetup ? "Admin Login" : "Admin Setup"}
            </CardTitle>
            <CardDescription className="text-slate-400">
              {isSetup 
                ? "Enter your credentials to access the admin dashboard"
                : "Create your admin account to get started"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="admin-email-input"
                  placeholder="admin@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    data-testid="admin-password-input"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {!isSetup && (
                  <p className="text-xs text-slate-500">
                    Choose a strong password for your admin account
                  </p>
                )}
              </div>

              <Button
                type="submit"
                data-testid="admin-login-btn"
                disabled={loading}
                className="w-full bg-bronze hover:bg-amber-600 text-white"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                {isSetup ? "Login" : "Create Admin Account"}
              </Button>
            </form>

            {!isSetup && (
              <div className="mt-4 p-3 bg-slate-700/50 rounded-lg">
                <p className="text-xs text-slate-400 text-center">
                  <Shield className="w-3 h-3 inline mr-1" />
                  This is a one-time setup. You can only create one admin account.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          <a href="/" className="text-bronze hover:underline">← Back to Website</a>
        </p>
      </div>
    </div>
  );
}
